import { FlightIcon } from '@/assets/icons'
import PageLoader from '@components/PageLoader'
import { useBreadcrumbs } from '@context/BreadcrumbContext'
import { useAuth } from '@context/AuthContext'
import { useFlights, useTrip, useTripMembers } from '@hooks'
import { GetFlightGroupResponse, GetUserType } from '@orbit/api-types'
import React, { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import AddFlightModal from './AddFlightModal'
import {
	getArrivalDisplay,
	getDepartureDisplay,
	getFlightCode,
	sortGroupsByDeparture,
} from './flightHelpers'
import {
	AddButton,
	Avatar,
	AvatarStack,
	ContentContainer,
	EmptyState,
	EmptyStateButton,
	EmptyStateContainer,
	EmptyStateIcon,
	EmptyStateText,
	ErrorContainer,
	FlightCount,
	FlightGroupCard,
	FlightGroupGrid,
	FlightGroupHeader,
	FlightGroupTitle,
	FlightsPageContainer,
	HeaderRow,
	LoadingContainer,
	ManualPill,
	OverflowAvatar,
	PageTitle,
	PassengerCount,
	PassengerRow,
	RouteAirport,
	RouteArrow,
	RouteBlock,
	RouteEndpoint,
	RouteLabel,
	RouteTime,
	YoureAPassengerPill,
} from './FlightsPage.styles'

const MAX_VISIBLE_AVATARS = 4

const getInitials = (member: GetUserType): string => {
	const first = member.firstName?.[0] ?? ''
	const last = member.lastName?.[0] ?? ''
	return (first + last).toUpperCase() || '?'
}

const getGroupPassengerIds = (group: GetFlightGroupResponse): number[] => {
	const ids = new Set<number>()
	for (const leg of group.flights) {
		for (const p of leg.passengers) {
			ids.add(p.userId)
		}
	}
	return Array.from(ids)
}

const FlightsPage: React.FC = () => {
	const { tripId } = useParams<{ tripId: string }>()
	const navigate = useNavigate()
	const { user } = useAuth()
	const { data: trip, isLoading: tripLoading, error: tripError } = useTrip(tripId)
	const { data: flightsData, isLoading: flightsLoading, error: flightsError } = useFlights(tripId)
	const { data: membersData } = useTripMembers(tripId)

	const [isAddModalOpen, setIsAddModalOpen] = useState(false)

	const members = membersData?.members ?? []
	const flightGroups = flightsData?.flightGroups ?? []

	useBreadcrumbs(
		trip
			? [
					{ label: 'My Trips', path: '/trips' },
					{ label: trip.name, path: `/trips/${tripId}` },
					{ label: 'Flights' },
				]
			: []
	)

	const getMemberById = (userId: number): GetUserType | undefined => {
		return members.find(m => m.id === userId)
	}

	const sortedGroups = sortGroupsByDeparture(flightGroups)

	if (tripLoading || flightsLoading) {
		return (
			<LoadingContainer>
				<PageLoader label="Loading flights..." />
			</LoadingContainer>
		)
	}

	if (tripError || flightsError) {
		return (
			<ErrorContainer>
				Error loading flights: {tripError?.message || flightsError?.message}
			</ErrorContainer>
		)
	}

	if (!trip) {
		return <LoadingContainer>Trip not found</LoadingContainer>
	}

	return (
		<FlightsPageContainer>
			{flightGroups.length === 0 ? (
				<EmptyStateContainer>
					<EmptyState>
						<EmptyStateIcon>
							<FlightIcon width={128} height={128} fill="currentColor" />
						</EmptyStateIcon>
						<EmptyStateText>
							No flights yet. Add your first flight so everyone knows when you're arriving and departing.
						</EmptyStateText>
						<EmptyStateButton onClick={() => setIsAddModalOpen(true)}>
							Add Flight
						</EmptyStateButton>
					</EmptyState>
				</EmptyStateContainer>
			) : (
				<ContentContainer>
					<HeaderRow>
						<div>
							<PageTitle>Flights</PageTitle>
							<FlightCount>
								{flightGroups.length} {flightGroups.length === 1 ? 'flight' : 'flights'}
							</FlightCount>
						</div>
						<AddButton onClick={() => setIsAddModalOpen(true)}>
							+ Add Flight
						</AddButton>
					</HeaderRow>

					<FlightGroupGrid>
						{sortedGroups.map(group => {
							const passengerIds = getGroupPassengerIds(group)
							const visibleIds = passengerIds.slice(0, MAX_VISIBLE_AVATARS)
							const overflowCount = passengerIds.length - MAX_VISIBLE_AVATARS
							const isUserPassenger = user ? passengerIds.includes(user.id) : false

							const firstLeg = group.flights[0]
							const lastLeg = group.flights[group.flights.length - 1]
							const hasManual = group.flights.some(l => l.isManuallyAdded)

							const titleCode = firstLeg ? getFlightCode(firstLeg) : 'Flight'
							const depCode = firstLeg?.departureAirportCode ?? firstLeg?.departureAirport?.iataCode ?? 'N/A'
							const arrCode = lastLeg?.arrivalAirportCode ?? lastLeg?.arrivalAirport?.iataCode ?? 'N/A'
							const depStr = firstLeg ? getDepartureDisplay(firstLeg) : null
							const arrStr = lastLeg ? getArrivalDisplay(lastLeg) : null

							return (
								<FlightGroupCard
									key={group.id}
									onClick={() => navigate(`/trips/${tripId}/flights/${group.id}`)}
								>
									<FlightGroupHeader>
										<FlightGroupTitle>{titleCode}</FlightGroupTitle>
										{hasManual && <ManualPill>Manual</ManualPill>}
									</FlightGroupHeader>

									<RouteBlock>
										<RouteEndpoint $align="start">
											<RouteAirport>{depCode}</RouteAirport>
											<RouteLabel>Departs</RouteLabel>
											<RouteTime>{depStr ?? 'N/A'}</RouteTime>
										</RouteEndpoint>
										<RouteArrow>
											<FlightIcon width={14} height={14} fill="currentColor" />
										</RouteArrow>
										<RouteEndpoint $align="end">
											<RouteAirport>{arrCode}</RouteAirport>
											<RouteLabel>Arrives</RouteLabel>
											<RouteTime>{arrStr ?? 'N/A'}</RouteTime>
										</RouteEndpoint>
									</RouteBlock>

									<PassengerRow>
										<AvatarStack>
											{visibleIds.map((userId, index) => {
												const member = getMemberById(userId)
												if (!member) return null
												return (
													<Avatar key={userId} $index={index}>
														{member.profilePicture ? (
															<img src={member.profilePicture} alt={getInitials(member)} />
														) : (
															getInitials(member)
														)}
													</Avatar>
												)
											})}
											{overflowCount > 0 && (
												<OverflowAvatar $index={MAX_VISIBLE_AVATARS}>
													+{overflowCount}
												</OverflowAvatar>
											)}
										</AvatarStack>
										<PassengerCount>
											{passengerIds.length} {passengerIds.length === 1 ? 'passenger' : 'passengers'}
										</PassengerCount>
										{isUserPassenger && (
											<YoureAPassengerPill>You're a passenger</YoureAPassengerPill>
										)}
									</PassengerRow>
								</FlightGroupCard>
							)
						})}
					</FlightGroupGrid>
				</ContentContainer>
			)}

			<AddFlightModal
				isOpen={isAddModalOpen}
				onClose={() => setIsAddModalOpen(false)}
				members={members}
			/>
		</FlightsPageContainer>
	)
}

export default FlightsPage
